import { useState } from "react"
import { withStyles } from "@mui/styles"
import {
    Grid,
    TextField,
    InputAdornment,
    Button,
    Box,
    IconButton,
    CircularProgress,
    Typography,
    useTheme
} from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { Modal } from "../Modal/Modal"
import { ModalBody } from "../Modal/ModalBody"
import { ModalHeader } from "../Modal/ModalHeader"
import { useUserStore } from "../../../hooks/UseUserStore"
import { useAuthStore } from "../../../hooks/useAuthStore"
import { ApiStatus } from "../../../types/api/status"

const CssTextField = withStyles({
    root: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#e3e1e1',
            },
            '&:hover fieldset': {
                borderColor: '#b0bec5',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#4c94b6',
            },
        },
    },
})(TextField)

export const ModalUpdatePassword: React.FC<any> = ({ open, closeModal, logout }) => {
    const theme = useTheme()

    const { status: statusUser, changePassword } = useUserStore()
    const { user } = useAuthStore()

    const [oldPassword, setOldPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [verifyPassword, setVerifyPassword] = useState("")
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [showPassword, setShowPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showVerifyPassword, setShowVerifyPassword] = useState(false)

    const handleValidation = () => {
        const newErrors: Record<string, string> = {}
        let formIsValid = true

        if (!oldPassword) {
            formIsValid = false
            newErrors["oldPassword"] = 'Contraseña actual requerida'
        }

        if (!newPassword) {
            formIsValid = false
            newErrors["newPassword"] = 'Nueva contraseña requerida'
        }

        if (!verifyPassword) {
            formIsValid = false
            newErrors["verifyPassword"] = 'Verificación requerida'
        } else if (newPassword !== verifyPassword) {
            formIsValid = false
            newErrors["verifyPassword"] = 'Las contraseñas no coinciden'
        }

        setErrors(newErrors)
        return formIsValid
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const data = {
            password: oldPassword,
            new_password: newPassword,
        }

        if (handleValidation()) {
            const response = await changePassword(data)
            if (response === true) {
                closeModal?.()
                logout?.()
            }
        }
    }

    const renderPasswordField = (
        id: string,
        label: string,
        value: string,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        show: boolean,
        setShow: (b: boolean) => void,
        error?: string
    ) => (
        <Box mb={2} display="flex" flexDirection="column" alignItems="center">
            <CssTextField
                id={id}
                type={show ? "text" : "password"}
                size="small"
                value={value}
                onChange={onChange}
                placeholder={label}
                fullWidth
                style={{ maxWidth: 360 }}
                autoComplete="new-password"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShow(!show)} edge="end" sx={{ color: '#4c94b6' }}>
                                {show ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            {error && (
                <Typography variant="caption" color="error" mt={0.5} textAlign="center">
                    {error}
                </Typography>
            )}
        </Box>
    )

    return (
        <Modal open={open} handleClose={closeModal} disableEscapeKeyDown disableBackdropClick size="xs">
            <ModalHeader text="Cambiar contraseña" className="positionElements" onCancel={closeModal} />
            <ModalBody>
                <form onSubmit={handleSubmit}>
                    <Grid container direction="column" alignItems="center" spacing={2}>
                        <Grid item xs={12}>
                            {renderPasswordField(
                                "oldPassword",
                                "Ingrese contraseña actual",
                                oldPassword,
                                (e) => setOldPassword(e.target.value),
                                showPassword,
                                setShowPassword,
                                errors.oldPassword
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {renderPasswordField(
                                "newPassword",
                                "Ingrese nueva contraseña",
                                newPassword,
                                (e) => setNewPassword(e.target.value),
                                showNewPassword,
                                setShowNewPassword,
                                errors.newPassword
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {renderPasswordField(
                                "verifyNewPassword",
                                "Vuelva a ingresar nueva contraseña",
                                verifyPassword,
                                (e) => setVerifyPassword(e.target.value),
                                showVerifyPassword,
                                setShowVerifyPassword,
                                errors.verifyPassword
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                width="100%"
                                borderTop={`1px solid ${theme.palette.grey[300]}`}
                                pt={2}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={statusUser === ApiStatus.FETCHING}
                                    size="small"
                                >
                                    Cambiar contraseña
                                    {statusUser === ApiStatus.FETCHING && (
                                        <CircularProgress color="inherit" size={14} sx={{ ml: 1 }} />
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </ModalBody>
        </Modal>
    )
}